import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const TimeToHireChart = ({ data, description }) => {
  // Data preparation for chart
  const timeToHireByDepartmentData = () => {
    const departmentGroups = {};
    
    // Filter valid candidates with department and timeToHire data
    data
      .filter(candidate => 
        candidate.stage === 'Hired' && 
        candidate.timeToHire && 
        candidate.timeToHire > 0 && 
        (candidate.department || candidate.role) && 
        (candidate.department?.trim() !== '' || candidate.role?.trim() !== '')
      )
      .forEach(candidate => {
        // Use department as the key, fallback to role if department not available
        const department = (candidate.department || candidate.role || '').trim();
        
        if (!departmentGroups[department]) {
          departmentGroups[department] = {
            totalDays: 0,
            count: 0
          };
        }
        
        // Parse timeToHire as number to ensure proper calculation
        const timeToHire = parseInt(candidate.timeToHire) || 0;
        if (timeToHire > 0) {
          departmentGroups[department].totalDays += timeToHire;
          departmentGroups[department].count += 1;
        }
      });
    
    // Convert to array and sort by days (ascending)
    return Object.entries(departmentGroups)
      .filter(([department, data]) => department && data.count > 0) // Filter out empty departments
      .map(([department, data]) => ({
        name: department.length > 20 ? department.substring(0, 18) + '...' : department, // Truncate long department names
        fullName: department, // Keep full name for tooltip
        days: Math.round(data.totalDays / data.count),
        count: data.count // Include count for tooltip
      }))
      .sort((a, b) => a.days - b.days) // Sort by days ascending
      .slice(0, 10); // Limit to top 10 departments
  };

  return (
    <div className="dashboard-card p-6 slide-in-up" style={{animationDelay: '500ms'}}>
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Time to Hire by Department</h3>
        <div className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-1 rounded-full">
          Average Days
        </div>
      </div>
      {description && (
        <p className="text-sm text-gray-600 mb-4">{description}</p>
      )}
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={timeToHireByDepartmentData()} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
          <YAxis tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
          <Tooltip 
            contentStyle={{ 
              borderRadius: '8px', 
              border: 'none', 
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              fontSize: '12px',
              padding: '8px 12px'
            }} 
            formatter={(value, name, props) => {
              if (name === 'days') {
                return [`${value} days`, 'Time to Hire'];
              }
              return [value, name];
            }}
            labelFormatter={(label, items) => {
              // Use fullName from payload if available
              const item = items[0]?.payload;
              return item?.fullName || label;
            }}
          />
          <Bar 
            dataKey="days" 
            fill="#10b981" 
            name="Average Days"
            radius={[4, 4, 0, 0]}
            barSize={30}
          >
            {timeToHireByDepartmentData().map((_, index) => (
              <Cell key={`cell-${index}`} fill={`#10b981${90 - index * 10}`} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TimeToHireChart;
